/**
 * Tarjeta de Expedientes Relacionados.
 *
 * Muestra:
 *   - Expedientes relacionados (tabla expediente_relaciones, bidireccional).
 *   - Reclamaciones hipotecarias vinculadas (reclamacion.expediente_id == expedienteId).
 *
 * "Añadir relación" permite elegir el tipo de entidad a vincular:
 *   - Expediente: crea relación en expediente_relaciones.
 *   - Reclamación hipotecaria: asigna expediente_id a la reclamación.
 *
 * Props:
 *   - expedienteId (number): id del expediente actual.
 *   - canEdit (bool): si el usuario puede modificar las relaciones.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link2, Plus, X, Search, Loader2, ExternalLink, Scale } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { obtenerToken } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = obtenerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const TIPOS_RELACION = [
  { value: "principal", label: "Principal (matriz)" },
  { value: "derivado", label: "Derivado / Secundario" },
  { value: "recurso_apelacion", label: "Recurso de apelación" },
  { value: "recurso_casacion", label: "Recurso de casación" },
  { value: "ejecucion", label: "Ejecución" },
  { value: "pieza_separada", label: "Pieza separada" },
  { value: "otro", label: "Otro" },
];

const TIPO_COLORS = {
  principal: "bg-indigo-100 text-indigo-800 border-indigo-200",
  derivado: "bg-sky-100 text-sky-800 border-sky-200",
  recurso_apelacion: "bg-amber-100 text-amber-800 border-amber-200",
  recurso_casacion: "bg-orange-100 text-orange-800 border-orange-200",
  ejecucion: "bg-rose-100 text-rose-800 border-rose-200",
  pieza_separada: "bg-purple-100 text-purple-800 border-purple-200",
  otro: "bg-gray-100 text-gray-700 border-gray-200",
};

// Tipos de entidad relacionable (ampliable a futuro: consumo, etc.)
const TIPOS_ENTIDAD = [
  { value: "expediente", label: "Expediente" },
  { value: "hipotecaria", label: "Reclamación hipotecaria" },
];

function tipoLabel(t) {
  return TIPOS_RELACION.find((x) => x.value === t)?.label || t;
}

export default function ExpedientesRelacionadosCard({ expedienteId, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [hipotecarias, setHipotecarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);

  const cargar = useCallback(async () => {
    if (!expedienteId) return;
    setLoading(true);
    try {
      const [rel, hip] = await Promise.all([
        axios
          .get(`${API}/expedientes/${expedienteId}/relacionados`, { headers: authHeaders() })
          .then((r) => r.data || [])
          .catch((e) => {
            console.error("Error cargando relacionados", e);
            return [];
          }),
        axios
          .get(`${API}/extrajudicial/por-expediente/${expedienteId}`, { headers: authHeaders() })
          .then((r) => r.data?.resultados || [])
          .catch((e) => {
            console.error("Error cargando hipotecarias", e);
            return [];
          }),
      ]);
      setItems(rel);
      setHipotecarias(hip);
    } finally {
      setLoading(false);
    }
  }, [expedienteId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const eliminar = async (relacionId) => {
    if (!window.confirm("¿Quitar este expediente relacionado?")) return;
    try {
      await axios.delete(`${API}/expedientes/relaciones/${relacionId}`, { headers: authHeaders() });
      toast.success("Relación eliminada");
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando relación");
    }
  };

  const desvincularHipotecaria = async (recId) => {
    if (!window.confirm("¿Desvincular esta reclamación hipotecaria del expediente?")) return;
    try {
      await axios.post(
        `${API}/extrajudicial/reclamaciones/${recId}/desasociar-expediente`,
        {},
        { headers: authHeaders() }
      );
      toast.success("Reclamación desvinculada");
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error desvinculando");
    }
  };

  const total = items.length + hipotecarias.length;

  return (
    <div className="space-y-3" data-testid="expedientes-relacionados-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Link2 className="w-4 h-4" />
          {loading ? (
            <span>Cargando…</span>
          ) : total === 0 ? (
            <span>No hay expedientes relacionados</span>
          ) : (
            <span>
              {total} relación{total === 1 ? "" : "es"} ({items.length} expediente
              {items.length === 1 ? "" : "s"}, {hipotecarias.length} hipotecaria
              {hipotecarias.length === 1 ? "" : "s"})
            </span>
          )}
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenAdd(true)}
            data-testid="btn-add-relacion-expediente"
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir relación
          </Button>
        )}
      </div>

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="lista-expedientes-relacionados">
          {items.map((it) => (
            <div
              key={it.relacion_id}
              className="flex items-start justify-between gap-3 p-3 border rounded-lg bg-gray-50 hover:bg-white transition-colors"
              data-testid={`relacion-${it.relacion_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`/expedientes/${it.expediente_id}`}
                    className="font-semibold text-blue-700 hover:underline flex items-center gap-1"
                  >
                    {it.numero_expediente}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <Badge
                    variant="outline"
                    className={`text-xs ${TIPO_COLORS[it.tipo_relacion] || TIPO_COLORS.otro}`}
                  >
                    {tipoLabel(it.tipo_relacion)}
                  </Badge>
                </div>
                <div className="text-sm text-gray-800 mt-1 truncate" title={it.titulo}>
                  {it.titulo}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {it.cliente_nombre && <>Cliente: {it.cliente_nombre} · </>}
                  {it.fase && <>Fase: {it.fase} · </>}
                  Estado: {it.estado}
                </div>
                {it.notas && (
                  <div className="text-xs text-gray-600 mt-2 italic whitespace-pre-wrap">
                    {it.notas}
                  </div>
                )}
              </div>
              {canEdit && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-500 hover:bg-red-50"
                  onClick={() => eliminar(it.relacion_id)}
                  data-testid={`btn-quitar-relacion-${it.relacion_id}`}
                  title="Quitar relación"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && hipotecarias.length > 0 && (
        <div className="space-y-2" data-testid="lista-hipotecarias-relacionadas">
          {hipotecarias.map((h) => (
            <div
              key={`hip-${h.id}`}
              className="flex items-start justify-between gap-3 p-3 border rounded-lg bg-indigo-50/60 hover:bg-white transition-colors"
              data-testid={`hipotecaria-${h.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`/extrajudicial/${h.id}`}
                    className="font-semibold text-indigo-700 hover:underline flex items-center gap-1"
                  >
                    {h.numero_referencia}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <Badge variant="outline" className="text-xs bg-indigo-100 text-indigo-800 border-indigo-200">
                    <Scale className="w-3 h-3 mr-1" /> Reclamación hipotecaria
                  </Badge>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {h.cliente_nombre && <>Cliente: {h.cliente_nombre} · </>}
                  Estado: {h.estado}
                </div>
              </div>
              {canEdit && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-500 hover:bg-red-50"
                  onClick={() => desvincularHipotecaria(h.id)}
                  data-testid={`btn-quitar-hipotecaria-${h.id}`}
                  title="Desvincular reclamación"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {openAdd && (
        <AddRelacionDialog
          expedienteId={expedienteId}
          excluirIds={items.map((i) => i.expediente_id).concat(expedienteId)}
          excluirHipotecariasIds={hipotecarias.map((h) => h.id)}
          onClose={() => setOpenAdd(false)}
          onSaved={() => {
            setOpenAdd(false);
            cargar();
          }}
        />
      )}
    </div>
  );
}

function AddRelacionDialog({ expedienteId, excluirIds, excluirHipotecariasIds, onClose, onSaved }) {
  const [entidad, setEntidad] = useState("expediente");
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [searching, setSearching] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [tipo, setTipo] = useState("otro");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset al cambiar el tipo de entidad
  useEffect(() => {
    setQuery("");
    setResultados([]);
    setSeleccionado(null);
  }, [entidad]);

  // Búsqueda con debounce
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResultados([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        if (entidad === "expediente") {
          const r = await axios.get(`${API}/expedientes`, {
            headers: authHeaders(),
            params: { search: query.trim(), limit: 30 },
          });
          let arr = [];
          if (Array.isArray(r.data)) arr = r.data;
          else if (Array.isArray(r.data?.items)) arr = r.data.items;
          else if (Array.isArray(r.data?.expedientes)) arr = r.data.expedientes;
          else if (Array.isArray(r.data?.data)) arr = r.data.data;
          const excluir = Array.isArray(excluirIds) ? excluirIds : [];
          setResultados(arr.filter((e) => !excluir.includes(e.id)));
        } else {
          const r = await axios.get(`${API}/extrajudicial/reclamaciones`, {
            headers: authHeaders(),
            params: { search: query.trim(), limit: 30 },
          });
          const arr = Array.isArray(r.data) ? r.data : r.data?.items || [];
          const excluir = Array.isArray(excluirHipotecariasIds) ? excluirHipotecariasIds : [];
          setResultados(arr.filter((e) => !excluir.includes(e.id)));
        }
      } catch (e) {
        console.error("[Relacionados] búsqueda error:", e?.response?.status, e?.message);
        try {
          toast.error(`Error buscando: ${e?.response?.data?.detail || e.message}`);
        } catch (_) {}
        setResultados([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, entidad, excluirIds, excluirHipotecariasIds]);

  const guardar = async () => {
    if (!seleccionado) {
      toast.error("Selecciona una entidad");
      return;
    }
    setSaving(true);
    try {
      if (entidad === "expediente") {
        await axios.post(
          `${API}/expedientes/${expedienteId}/relacionados`,
          {
            expediente_relacionado_id: seleccionado.id,
            tipo_relacion: tipo,
            notas: notas.trim() || null,
          },
          { headers: authHeaders() }
        );
        toast.success("Expediente relacionado añadido");
      } else {
        await axios.post(
          `${API}/extrajudicial/reclamaciones/${seleccionado.id}/asociar-expediente`,
          { expediente_id: expedienteId },
          { headers: authHeaders() }
        );
        toast.success("Reclamación hipotecaria vinculada");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error añadiendo relación");
    } finally {
      setSaving(false);
    }
  };

  const esExp = entidad === "expediente";

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="dialog-add-relacion">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" /> Añadir relación
          </DialogTitle>
          <DialogDescription>
            Vincula el expediente actual con otro expediente del despacho o con una
            reclamación hipotecaria extrajudicial.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="tipo-entidad">Tipo de entidad a relacionar</Label>
            <Select value={entidad} onValueChange={setEntidad}>
              <SelectTrigger id="tipo-entidad" data-testid="select-tipo-entidad">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_ENTIDAD.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!seleccionado ? (
            <>
              <div>
                <Label htmlFor="search-relacion">
                  {esExp ? "Buscar expediente" : "Buscar reclamación hipotecaria"}
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="search-relacion"
                    autoFocus
                    placeholder={
                      esExp
                        ? "Nº expediente, título o cliente…"
                        : "Nº referencia (EXT-…), cliente o banco…"
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9"
                    data-testid="input-buscar-relacion"
                  />
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto border rounded-lg divide-y bg-white">
                {searching && (
                  <div className="p-4 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
                  </div>
                )}
                {!searching && query.length >= 2 && resultados.length === 0 && (
                  <div className="p-4 text-center text-sm text-gray-500">Sin resultados</div>
                )}
                {!searching && query.length < 2 && (
                  <div className="p-4 text-center text-sm text-gray-400">
                    Escribe al menos 2 caracteres…
                  </div>
                )}
                {resultados.map((e) =>
                  esExp ? (
                    <button
                      key={e.id}
                      type="button"
                      className="w-full text-left p-3 hover:bg-blue-50 transition-colors"
                      onClick={() => setSeleccionado(e)}
                      data-testid={`resultado-expediente-${e.id}`}
                    >
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-semibold text-blue-700 whitespace-nowrap">
                          {e.numero_expediente}
                        </span>
                        {e.estado && (
                          <span className="text-[10px] uppercase bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                            {e.estado}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-900 mt-0.5 break-words">{e.titulo}</div>
                      <div className="text-xs text-gray-500 mt-0.5 break-words">
                        {e.cliente_nombre && (
                          <>
                            Cliente: <span className="text-gray-700">{e.cliente_nombre}</span> ·{" "}
                          </>
                        )}
                        {e.rama && <>{e.rama} · </>}
                        {e.fase}
                      </div>
                    </button>
                  ) : (
                    <button
                      key={e.id}
                      type="button"
                      className="w-full text-left p-3 hover:bg-indigo-50 transition-colors"
                      onClick={() => setSeleccionado(e)}
                      data-testid={`resultado-hipotecaria-${e.id}`}
                    >
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-semibold text-indigo-700 whitespace-nowrap">
                          {e.numero_referencia}
                        </span>
                        {e.estado && (
                          <span className="text-[10px] uppercase bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                            {e.estado}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 break-words">
                        {e.cliente_nombre && (
                          <>
                            Cliente: <span className="text-gray-700">{e.cliente_nombre}</span> ·{" "}
                          </>
                        )}
                        {e.entidad_bancaria_actual}
                      </div>
                    </button>
                  )
                )}
              </div>
            </>
          ) : (
            <div className="p-3 border-2 border-blue-200 rounded-lg bg-blue-50">
              <div className="text-xs text-gray-500">
                {esExp ? "Expediente seleccionado" : "Reclamación seleccionada"}
              </div>
              <div className="font-semibold text-blue-700">
                {esExp ? seleccionado.numero_expediente : seleccionado.numero_referencia}
              </div>
              {esExp && <div className="text-sm">{seleccionado.titulo}</div>}
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setSeleccionado(null)}>
                Cambiar selección
              </Button>
            </div>
          )}

          {esExp && (
            <>
              <div>
                <Label htmlFor="tipo-relacion">Tipo de relación</Label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger id="tipo-relacion" data-testid="select-tipo-relacion">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_RELACION.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notas-relacion">Notas (opcional)</Label>
                <Textarea
                  id="notas-relacion"
                  rows={3}
                  placeholder="Ej: recurso de apelación interpuesto el 12/05/2026 ante la AP de Barcelona"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  data-testid="textarea-notas-relacion"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={guardar}
            disabled={!seleccionado || saving}
            data-testid="btn-guardar-relacion"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
            Guardar relación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
